import React, { useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DataTableFilter } from "@/components/controls/DataTableFilters/DataTableFilters";
import { Filters, FiltersSetter } from "@/services/api/types";
import Text from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import FilterMenuItem from "@/components/controls/ToolbarActions/ToolbarFilterButton/FilterMenuItem";
import {
  TextCondition,
  TextFilterValue,
  TEXT_CONDITIONS,
  TEXT_FILTER_TRANSLATION_KEY,
  parseTextFilterValue,
  buildTextFilterValue
} from "@/components/controls/ToolbarActions/ToolbarFilterButton/types";
import Input from "@/components/ui/input";
import { Check } from "lucide-react";

interface Props {
  filterOption: DataTableFilter;
  filterLabel: string;
  filters: Filters;
  setFilters: FiltersSetter;
  onClose?: () => void;
}

const CONDITIONS_WITHOUT_VALUE: TextCondition[] = ["isEmpty", "isNotEmpty"];

const FilterTextSubmenu: React.FC<Props> = ({
  filterOption,
  filterLabel,
  filters,
  setFilters,
  onClose
}) => {
  const { t } = useTranslation();
  const filterKey = filterOption.key;

  const currentValue = filters[filterKey as keyof Filters] as
    | TextFilterValue
    | undefined;

  const parsedValue = currentValue ? parseTextFilterValue(currentValue) : null;
  const initialCondition = parsedValue?.condition ?? null;

  const [selectedCondition, setSelectedCondition] =
    useState<TextCondition | null>(initialCondition);
  const [showValueInput, setShowValueInput] = useState(
    initialCondition !== null &&
      !CONDITIONS_WITHOUT_VALUE.includes(initialCondition)
  );

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (showValueInput) {
      inputRef.current?.focus();
    }
  }, [showValueInput]);

  const handleSelectCondition = (condition: TextCondition) => {
    setSelectedCondition(condition);

    // For isEmpty/isNotEmpty, apply immediately without value input
    if (CONDITIONS_WITHOUT_VALUE.includes(condition)) {
      setFilters((prev) => ({
        ...prev,
        [filterKey]: buildTextFilterValue(condition)
      }));
      onClose?.();
    } else {
      setShowValueInput(true);
    }
  };

  const handleApply = () => {
    if (!selectedCondition) return;

    const inputValue = inputRef.current?.value?.trim();

    // For conditions that require a value, ensure we have one
    if (!CONDITIONS_WITHOUT_VALUE.includes(selectedCondition) && !inputValue) {
      return;
    }

    setFilters((prev) => ({
      ...prev,
      [filterKey]: buildTextFilterValue(selectedCondition, inputValue)
    }));
    onClose?.();
  };

  const handleCancel = () => {
    if (showValueInput) {
      setShowValueInput(false);
      setSelectedCondition(null);
    } else {
      onClose?.();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleApply();
    }
  };

  const getConditionLabel = (condition: TextCondition) =>
    t(`${TEXT_FILTER_TRANSLATION_KEY}.${condition}`);

  const isConditionSelected = useMemo(() => {
    return (condition: TextCondition) => parsedValue?.condition === condition;
  }, [parsedValue]);

  if (showValueInput && selectedCondition) {
    const conditionLabel = getConditionLabel(selectedCondition);

    return (
      <div className="flex flex-col gap-1 min-w-[200px]">
        <Text variant="bodySecondary" className="w-full p-2 pr-3 pb-[2px]">
          {`${filterLabel} ${conditionLabel.toLowerCase()}`}
        </Text>
        <div className="flex flex-col gap-2 py-2 px-1">
          <Input
            ref={inputRef}
            placeholder={t("datatoolbar.filters.enterValue")}
            defaultValue={parsedValue?.value ?? ""}
            onKeyDown={handleKeyDown}
          />
        </div>
        <div className="flex justify-end gap-2 p-1">
          <Button onClick={handleCancel} variant="secondary">
            {t("cancel")}
          </Button>
          <Button onClick={handleApply}>{t("apply")}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1 min-w-[160px]">
      {TEXT_CONDITIONS.map((condition) => {
        const isSelected = isConditionSelected(condition);
        return (
          <FilterMenuItem
            key={condition}
            onClick={() => handleSelectCondition(condition)}
            isActive={isSelected}
          >
            <div className="flex items-center justify-between w-full">
              <Text variant="body">{getConditionLabel(condition)}</Text>
              {isSelected && <Check className="h-4 w-4 text-primary" />}
            </div>
          </FilterMenuItem>
        );
      })}
    </div>
  );
};

export default FilterTextSubmenu;
